import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, MessageSquare, Sparkles } from 'lucide-react';
import ReactionPicker from '@/components/user/ReactionPicker';
import ChapterComments from '@/components/capitulo/ChapterComments';

interface ChapterInteractionsProps {
    capitulo: any;
    infiniteScroll: boolean;
}

function getChapterId(capitulo: any): number {
    return Number(capitulo?.cap_id ?? capitulo?.id ?? 0);
}

export default function ChapterInteractions({ capitulo, infiniteScroll }: ChapterInteractionsProps) {
    const chapterId = getChapterId(capitulo);
    const [expanded, setExpanded] = useState(!infiniteScroll);
    const [commentsCount, setCommentsCount] = useState(0);

    const chapterLabel = useMemo(() => {
        const num = capitulo?.cap_num ?? capitulo?.numero;
        if (num != null && String(num).trim() !== '') return `Cap. ${num}`;
        return 'Capítulo';
    }, [capitulo]);

    useEffect(() => {
        setExpanded(!infiniteScroll);
    }, [infiniteScroll, chapterId]);

    if (!chapterId) return null;

    return (
        <section className="w-full mt-8 md:mt-10 border-t border-white/10 pt-5 md:pt-7 px-3 md:px-4">
            {infiniteScroll && !expanded ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">Interações</p>
                        <p className="text-sm text-white/85 truncate">
                            {chapterLabel} - {commentsCount} comentário(s)
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="shrink-0 px-3 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 text-xs font-semibold transition-colors flex items-center gap-1.5"
                    >
                        Exibir interações
                        <ChevronDown size={15} />
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {infiniteScroll && (
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">Interações</p>
                                <p className="text-sm text-white/85 truncate">{chapterLabel}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setExpanded(false)}
                                className="shrink-0 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs font-semibold transition-colors flex items-center gap-1.5"
                            >
                                Encolher
                                <ChevronUp size={15} />
                            </button>
                        </div>
                    )}

                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-white/90">
                            <Sparkles size={16} className="text-orange-400" />
                            <h3 className="text-sm font-semibold">Reações</h3>
                        </div>
                        <ReactionPicker entityId={chapterId} entityType="capitulo" variant={infiniteScroll ? 'compact' : 'default'} />
                    </div>

                    <div className="space-y-3">
                        <ChapterComments
                            chapterId={chapterId}
                            onCountUpdate={(count) => setCommentsCount(count)}
                        />
                    </div>
                </div>
            )}
        </section>
    );
}
